import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FormikProvider, useFormik } from "formik";
import { addDays, format, subDays } from "date-fns";
import CurrencyInput from 'react-currency-input-field';
import * as Yup from 'yup';
import {
  Box,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  Button,
  Grid,
  Typography,
  Tooltip,
  IconButton,
  FormGroup,
  FormControlLabel,
  Switch,
} from "@mui/material";

import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { ContratoTab } from "../../contratoTab";
import { ServicoTab } from "../../servicoTab";
import { BoxBase, CustomTabPanel, FormControlBase, FormSectionBox, FormSectionTitle, InputLabelSelect, SelectBase, tabProps, TextFieldBase, ButtonBase, CheckboxBase } from "../../../styles";
import { PedidosContext } from "../../../../../Context/PedidosContext";
import { MyContext } from "../../../../../Context/MyContext";

import { InscricoesAdicionais } from "../../inscricoesAdicionais";
import { ModalLiberacao } from "../../modalLiberacao";

import { Edit } from "@mui/icons-material";
import Loading from "../../../../../Components/Loading";
import Mensagem from "../../../../../Components/Mensagem";
import ModalNS from "../../../../../Components/ModalNS";
import api from "../../../../../Api/apiAxios";
import { LicencaSection } from "../../licencaSection";

const validationSchema = Yup.object({
  tipoPedido: Yup.number().nullable(),
  tipoPlano: Yup.number().nullable(),
  statusPedido: Yup.number().required('Status do pedido é obrigatório')
    .min(0)
    .nullable(false),
  distribuidor: Yup.number().required('Revenda é obrigatória'),
  cliente: Yup.number().required('Cliente é obrigatório'),
  nomeFantasia: Yup.string().required('Nome fantasia é obrigatório'),
  razaoSocial: Yup.string().nullable(),
  numeroInscricao: Yup.string().nullable(),
  numeroSerie: Yup.string().nullable(),
  modalidadeComercializacao: Yup.number().required('Modalidade de comercialização é obrigatória').oneOf([2], 'A modalidade de comercialização deve ser "Locação" para novos cadastros.'),
  tipoLicenciamento: Yup.number().nullable(),
  modalidadeServico: Yup.number().required('Modalidade de serviço é obrigatória'),
  plano: Yup.number().required('Plano é obrigatório'),
  bancoDeDados: Yup.string().required('Banco de dados é obrigatório'),
  observacoes: Yup.string().max(255, 'Observações não podem ter mais de 255 caracteres'),
  tipoServicoId: Yup.number().nullable().required('Tipo de Serviço é obrigatório'),
  observacaoNotaFiscal: Yup.string().nullable().max(1000, 'Observação de Nota Fiscal não pode ter mais de 1000 caracteres'),
  inicioFaturamento: Yup.date().required('O início do faturamento é obrigatório.'),
  faixaFuncionarios: Yup.number()
    .min(10, 'Faixa de funcionários deve ser maior ou igual a 10')
    .required('Faixa de funcionários é obrigatória'),
  usuariosAdicionais: Yup.number().nullable(),
  quantidadeLicencas: Yup.number().nullable(),
  valorPedido: Yup.string().nullable(),
  valorMensal: Yup.string().nullable(),
  portaria671: Yup.boolean(),
  integradorREP: Yup.boolean(),
  moduloEPI: Yup.boolean(),
  licenciado: Yup.boolean(),
  moduloAvaliacao: Yup.boolean(),
  moduloDocumentos: Yup.boolean(),
  acesso: Yup.boolean(),
  folha: Yup.boolean(),
  contrato: Yup.boolean(),
  versao: Yup.string().nullable(),
  release: Yup.string().nullable(),
  dataCancelamento: Yup.date().nullable().min(subDays(new Date(), 1), 'A data de cancelamento deve ser a partir de hoje.'),
  motivoCancelamentoOpcao: Yup.number().nullable(),
  motivoCancelamento: Yup.string().nullable(),
  dataPedido: Yup.date().nullable(),
  dataLiberacao: Yup.date().nullable(),
});

export function PedidoPonto({ isShow = false }) {
  const [value, setValue] = useState(0);
  const [tiposServico, setTiposServico] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [listaNS, setListaNS] = useState([]);
  const [selectedNS, setSelectedNS] = useState(false);
  const [showModalNS, setShowModalNS] = useState(false);
  const [showMensagem, setShowMensagem] = useState(false);
  const [mensagem, setMensagem] = useState('');
  const [tipoMensagem, setTipoMensagem] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const [isLicenciadoEnabled, setIsLicenciadoEnabled] = useState(false);
  const [isPlanoEnabled, setIsPlanoEnabled] = useState(true);
  const [lastLicenca, setLastLicenca] = useState({});
  const [isRevendaEnabled, setIsRevendaEnabled] = useState(true);
  const [initialValueFaixaFuncionarios, setInitialValueFaixaFuncionarios] = useState(0);
  const [initialValueUsuariosAdicionais, setInitialValueUsuariosAdicionais] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  const currencyFormatter = useMemo(() => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }), []);
  const [valorDiferencaMensal, setValorDiferencaMensal] = useState(currencyFormatter.format(0));
  const [valorContratoSituacao, setValorContratoSituacao] = useState(0);
  const [ultimoPedidoLicenca, setUltimoPedidoLicenca] = useState(null);

  const nivelUsuario = window.localStorage.getItem('nivelUsuario');
  const idUsuario = window.localStorage.getItem('idUsuario');
  const idDistribuidor = window.localStorage.getItem('distribuidorId');

  const tipoAtualizacaoCalculadoPeloUsuario = useRef(false);

  const navigate = useNavigate();

  const { id } = useParams();
  const {
    distribuidores,
    modalLiberacao,
    setModalLiberacao,
    setDadosTabs,
    motivosCancelamento,
    pedidoLiberado,
    setPedidoLiberado,
    parseNumberCurrency,
    getDistribuidores,
    tipoServicoPadrao,
  } = useContext(PedidosContext);
  const { setMensagemGlobal, setTipoMensagemGlobal } = useContext(MyContext);

  async function buscarValorContratoAtual(numeroSerie) {
    if (!numeroSerie || numeroSerie === 0 || numeroSerie === '0') return 0;

    try {
      const { data } = await api.get(`/Contrato/retornar-situacao-contrato?numeroSerie=${numeroSerie}`);
      
      return Number(data?.valorAtual) || 0;
    } catch (error) {
      setMensagemGlobal('Erro ao buscar contrato anterior.');
      setTipoMensagemGlobal('danger');
      return 0;
    }
  }

  const formik = useFormik({
    initialValues: {
      tipoPedido: 0,
      tipoPlano: 1,
      statusPedido: 0,
      distribuidor: null,
      cliente: null,
      nomeFantasia: '',
      razaoSocial: '',
      numeroInscricao: '',
      numeroSerie: 0,
      modalidadeComercializacao: 2,
      tipoLicenciamento: 2,
      modalidadeServico: null,
      plano: null,
      bancoDeDados: null,
      observacoes: '',
      tipoServicoId: null,
      observacaoNotaFiscal: '',
      versao: null,
      release: null,
      inicioFaturamento: format(new Date(), 'yyyy-MM-dd'),
      faixaFuncionarios: 25,
      usuariosAdicionais: 0,
      quantidadeLicencas: 0,
      valorPedido: "0,00",
      valorMensal: "0,00",
      tipoAtualizacao: 'A',
      portaria671: true,
      integradorREP: false,
      moduloEPI: false,
      licenciado: false,
      moduloAvaliacao: false,
      moduloDocumentos: false,
      acesso: false,
      folha: false,
      contrato: true,
      dataCancelamento: null,
      motivoCancelamentoOpcao: 0,
      motivoCancelamento: '',
      dataPedido: null,
      dataLiberacao: null,
    },
    validationSchema: validationSchema,
    onSubmit: async () => {
      if (isSavingRef.current) return;
      isSavingRef.current = true;
      setIsSaving(true);
      try {
        await handleSave();
      } finally {
        setIsSaving(false);
        isSavingRef.current = false;
      }
    },
  })

  const handleTabChange = (event, newValue) => {
    setValue(newValue);
  };

  async function handleDistribuidorChange(e) {
    formik.handleChange(e);
    formik.setFieldValue('cliente', 0);
    formik.setFieldValue('nomeFantasia', '');
    formik.setFieldValue('numeroSerie', 0);
    setListaNS([])
  }

  async function handleClienteChange(e) {
    formik.handleChange(e);
    formik.setFieldValue('nomeFantasia', '');
    formik.setFieldValue('numeroSerie', 0);
    setListaNS([])

    const idCliente = e.target.value;

    const response = await api.get(`/Cliente/${idCliente}`);

    formik.setFieldValue('nomeFantasia', response.data.nomeFantasia);
    formik.setFieldValue('numeroInscricao', response.data.numeroInscricao);

    loadNumeroSerie(response.data.id, formik.values.tipoPedido === 1);
  };

  async function handleVerificarTotaisClick() {
    setIsSaveEnabled(true);

    const tipoInstalacao = formik.values.tipoPlano ? formik.values.tipoPlano : 0;

    let regPedido = {
      Id: id || 0,
      FaixaFuncionarios: Number(formik.values.faixaFuncionarios) || 0,
      Portaria671: formik.values.portaria671 ? 1 : 0,
      IntegradorRep: formik.values.integradorREP ? 1 : 0,
      Supervisor: formik.values.plano === 2 ? 1 : 0,
      PortalColaborador: formik.values.plano === 2 ? 1 : 0,
      AplicativoRegistrador: formik.values.plano === 2 ? 1 : 0,
      ColetorVirtual: 0,
      QuantidadeColetorVirtual: 0,
      UsuariosAdicionais: Number(formik.values.usuariosAdicionais) || 0,
      ModuloEpi: formik.values.moduloEPI ? 1 : 0,
      TipoInstalacao: tipoInstalacao,
      TipoComercializacao: formik.values.modalidadeComercializacao,
      TipoLicenciamento: formik.values.tipoLicenciamento,
      TipoServicoPedido: formik.values.modalidadeServico,
      TipoPlano: formik.values.plano,
      DistribuidorId: Number(formik.values.distribuidor),
      TipoPedido: Number(formik.values.tipoPedido),
      ClienteId: Number(formik.values.cliente),
      NumeroSerie: Number(formik.values.numeroSerie),
      StatusPedido: formik.values.statusPedido || 0,
      TipoBancoDados: Number(formik.values.bancoDeDados),
      VersaoSistema: formik.values.versao.toString().includes('.')
        ? formik.values.versao
        : formik.values.versao.toString().substring(0, 2) +
        (formik.values.versao.toString().substring(2).length > 0
          ? '.' + formik.values.versao.toString().substring(2)
          : ''),
      VersaoRelease: formik.values.release,
      DataPedido: new Date().toISOString(),
      Observacao: formik.values.observacoes || '',
      NumeroOrdemCompra: '',
      ValorBruto: parseNumberCurrency(formik.values.valorPedido),
      ValorDesconto: 0,
      ValorLiquido: 0,
      ValorMensal: parseNumberCurrency(formik.values.valorMensal),
      IntegracaoAcesso: formik.values.acesso ? 1 : 0,
      IntegracaoFolha: formik.values.folha ? 1 : 0,
      IntegracaoPonto: 0,
      Contrato: formik.values.contrato ? 1 : 0,
      TipoAtualizacao: formik.values.tipoAtualizacao || 'A',
      EnviarEmailPedidoLiberado: 0,
      Licenciado: formik.values.licenciado ? 1 : 0,
      DataInicioFaturamento: formik.values.inicioFaturamento,
      DataCancelamento: formik.values.dataCancelamento,
      MotivoCancelamentoOpcao: formik.values.motivoCancelamentoOpcao,
      MotivoCancelamento: formik.values.motivoCancelamento || '',
    };

    const calculoUrl = ((regPedido.TipoComercializacao === 1 && regPedido.TipoLicenciamento === 2) || regPedido.Licenciado === 1) ? 'CalculoPonto/2024' : 'CalculoPonto/2025';
    const ret = await api.post(calculoUrl, JSON.stringify(regPedido), { headers: { 'Content-Type': 'application/json' } });

    formik.setFieldValue('valorPedido', ret.data.valorBruto);
    formik.setFieldValue('valorMensal', ret.data.valorMensal);
  }

  async function atualizarLicencaPorNS(fromBlur) {
    const { numeroSerie } = formik.values;

    if (!numeroSerie || numeroSerie === 0 || numeroSerie === '0') {
      formik.setFieldValue('tipoPedido', 0);
      return;
    }

    const { data } = await api.get(`/LicencaPonto/parametros-consulta?id=${idDistribuidor}&nivel=${nivelUsuario}&tipo=ns&args=${numeroSerie}`);

    const licenca = data.reduce((maior, atual) =>
      atual.id > (maior?.id || 0) ? atual : maior,
      null
    );

    if (!licenca) return;

    const valorContratoAtual = await buscarValorContratoAtual(licenca.numeroSerie);

    setLastLicenca(licenca);
    setIsLicenciadoEnabled(
      licenca?.tipoInstalacao === 0 &&
      valorContratoAtual > 0 &&
      (!licenca?.tipoComercializacao || (licenca?.tipoComercializacao === 1 && licenca?.tipoLicenciamento === 2))
    );
    if (licenca?.tipoInstalacao === 0) formik.setFieldValue('tipoLicenciamento', 1);
    formik.setFieldValue('tipoPedido', 1);

    setSelectedNS(false);

    loadFields({ ...licenca, valorContratoAtual }, fromBlur, true);
  }

  const handleSave = async () => {
    setMensagemGlobal(null);
    setTipoMensagemGlobal('')
    setShowMensagem(false)

    if (nivelUsuario !== '1') await handleVerificarTotaisClick();

    const tipoInstalacao = formik.values.plano ? formik.values.plano : 0;

    let diferencaValorMensalUltimaLicenca = 0;
    if (
      formik.values.tipoPedido === 1 &&
      lastLicenca &&
      Object.keys(lastLicenca).length > 0
    ) {
      const valorAtual = parseNumberCurrency(formik.values.valorMensal) || 0;
      const referenciaContrato = valorContratoSituacao || 0;
      diferencaValorMensalUltimaLicenca = valorAtual - referenciaContrato;
    }

    const changes = [
      {
        label: "Faixa de funcionários",
        initial: initialValueFaixaFuncionarios,
        current: formik.values.faixaFuncionarios,
      },
      {
        label: "Usuários adicionais",
        initial: initialValueUsuariosAdicionais,
        current: formik.values.usuariosAdicionais,
      },
    ];

    let details = "";

    changes.forEach(({ label, initial, current }) => {
      if (initial !== 0 && initial !== current) {
        details += ` - ${label} alterada de ${initial || 0} para ${current || 0}\n`;
      }
    });

    const observations = details ? (formik.values.observacoes || '') + '\n' + details : formik.values.observacoes;

    const regPedido = {
      Id: id || 0,
      FaixaFuncionarios: Number(formik.values.faixaFuncionarios) || 0,
      Portaria671: formik.values.portaria671 ? 1 : 0,
      IntegradorRep: formik.values.integradorREP ? 1 : 0,
      Supervisor: formik.values.plano === 2 ? 1 : 0,
      PortalColaborador: formik.values.plano === 2 ? 1 : 0,
      AplicativoRegistrador: formik.values.plano === 2 ? 1 : 0,
      ColetorVirtual: 0,
      QuantidadeColetorVirtual: 0,
      UsuariosAdicionais: Number(formik.values.usuariosAdicionais) || 0,
      ModuloEpi: formik.values.moduloEPI ? 1 : 0,
      TipoInstalacao: tipoInstalacao,
      TipoComercializacao: formik.values.modalidadeComercializacao,
      TipoLicenciamento: formik.values.modalidadeComercializacao === 1 ? formik.values.tipoLicenciamento : 2,
      TipoServicoPedido: formik.values.modalidadeServico,
      TipoPlano: formik.values.plano,
      DistribuidorId: Number(formik.values.distribuidor),
      TipoPedido: Number(formik.values.tipoPedido),
      ClienteId: Number(formik.values.cliente),
      NumeroSerie: Number(formik.values.numeroSerie),
      StatusPedido: formik.values.statusPedido || 0,
      TipoBancoDados: Number(formik.values.bancoDeDados),
      VersaoSistema: formik.values.versao.toString().includes('.')
        ? formik.values.versao
        : formik.values.versao.toString().substring(0, 2) +
        (formik.values.versao.toString().substring(2).length > 0
          ? '.' + formik.values.versao.toString().substring(2)
          : ''),
      VersaoRelease: formik.values.release,
      DataPedido: new Date().toISOString(),
      Observacao: observations || '',
      MotivoCancelamento: '',
      NumeroOrdemCompra: '',
      ValorBruto: parseNumberCurrency(formik.values.valorPedido),
      ValorDesconto: 0,
      ValorLiquido: 0,
      ValorMensal: parseNumberCurrency(formik.values.valorMensal),
      DiferencaValorMensalUltimaLicenca: diferencaValorMensalUltimaLicenca,
      IntegracaoAcesso: formik.values.acesso ? 1 : 0,
      IntegracaoFolha: formik.values.folha ? 1 : 0,
      IntegracaoPonto: 0,
      Contrato: formik.values.contrato ? 1 : 0,
      TipoAtualizacao: formik.values.tipoAtualizacao || 'A',
      EnviarEmailPedidoLiberado: 0,
      Licenciado: formik.values.licenciado ? 1 : 0,
      ModuloAvaliacao: formik.values.moduloAvaliacao ? 1 : 0,
      ModuloDocumentos: formik.values.moduloDocumentos ? 1 : 0,
      DataInicioFaturamento: new Date(formik.values.inicioFaturamento).toISOString(),
      DataCancelamento: formik.values.dataCancelamento,
      motivoCancelamentoOpcao: formik.values.motivoCancelamentoOpcao,
      motivoCancelamento: formik.values.motivoCancelamento || '',
      TipoServicoId: formik.values.tipoServicoId,
      ObservacaoNotaFiscal: formik.values.observacaoNotaFiscal || null,
    };

    if (regPedido.motivoCancelamento === undefined) regPedido.motivoCancelamento = null;
    if (regPedido.motivoCancelamentoOpcao === undefined) regPedido.motivoCancelamentoOpcao = null;

    if (regPedido.Observacao.length > 250) {
      regPedido.Observacao = regPedido.Observacao.slice(0, 250);
    }

    const camposParaVerificar = [
      'TipoInstalacao',
      'TipoComercializacao',
      'TipoLicenciamento',
      'TipoServicoPedido',
      'TipoPlano'
    ];

    const algumCampoFalta = camposParaVerificar.some(campo => {
      return regPedido[campo] === null || regPedido[campo] === undefined || regPedido[campo] === '';
    });

    if (algumCampoFalta) {
      const mensagemErro = "Alguns campos importantes do pedido estão faltando valores. Por favor, verifique e tente novamente.";
      console.error(mensagemErro + ' ' + JSON.stringify(regPedido));
    }

    try {
      let ret;

      if (id) {
        ret = await api.put("BmaPonto/", JSON.stringify(regPedido), { headers: { 'Content-Type': 'application/json' } });
      } else {
        ret = await api.post("BmaPonto/", JSON.stringify(regPedido), { headers: { 'Content-Type': 'application/json' } });
      }

      if (!id && ret.data.id > 0) {
        setMensagem('Pedido incluído com sucesso!');
        setTipoMensagem("success");
        setShowMensagem(true);
        navigate(`/viewpedidoponto/${ret.data.id}`)
      } else if (id) {
        setMensagem('Pedido alterado com sucesso!');
        setTipoMensagem("success");
        setShowMensagem(true);
        navigate(`/viewpedidoponto/${ret.data.id}`)
      }

    } catch (error) {
      console.error(error);
      setMensagem(error?.response?.data || error.message || 'Erro ao salvar o pedido.')
      setTipoMensagem("danger");
      setShowMensagem(true);
    }
  };

  const handleSelecionarNS = async () => {
    if (formik.values.tipoPedido !== 1) formik.setFieldValue('tipoPedido', 1)
    setSelectedNS(false)

    await loadNumeroSerie(formik.values.cliente, true)

    if (listaNS.length > 0) setShowModalNS(true)
  }

  async function loadNumeroSerie(clienteId, load) {
    const res = await api.get(`/LicencaPonto/retornar-ns-cliente?id=${clienteId}`);
    const qtdRegistros = res.data.length;

    formik.setFieldValue('quantidadeLicencas', qtdRegistros);

    if (load && qtdRegistros === 1) {
      const licenca = res.data[0];

      const valorContratoAtual = await buscarValorContratoAtual(licenca.numeroSerie);

      setLastLicenca(licenca);
      setIsLicenciadoEnabled(
        licenca?.tipoInstalacao === 0 &&
        valorContratoAtual > 0 &&
        (!licenca?.tipoComercializacao || (licenca?.tipoComercializacao === 1 && licenca?.tipoLicenciamento === 2))
      );
      if (licenca?.tipoInstalacao === 0) formik.setFieldValue('tipoLicenciamento', 1);
      formik.setFieldValue('tipoPedido', 1);

      setSelectedNS(false);

      loadFields({ ...licenca, valorContratoAtual }, true, true);
    } else if (qtdRegistros > 1) {
      setListaNS(res.data);
    } else if (formik.values.tipoPedido === 1) {
      formik.setFieldValue('tipoPedido', 0);
    }
  }

  const loadFields = async (values, allFields, fromNS = false) => {
    if (allFields) {
      if (!fromNS) formik.setFieldValue('tipoPedido', values.tipoPedido);
      formik.setFieldValue('statusPedido', values.statusPedido || 0);
      formik.setFieldValue('distribuidor', values.distribuidorId);
      formik.setFieldValue('cliente', values.clienteId);
      formik.setFieldValue('observacoes', values.observacao);
      formik.setFieldValue('tipoServicoId', values.tipoServicoId ?? null);
      formik.setFieldValue('observacaoNotaFiscal', values.observacaoNotaFiscal || '');
      formik.setFieldValue('valorPedido', values.valorBruto || values.valorPedido || 0);
      formik.setFieldValue('valorMensal', values.valorMensal || values.valorContratoAtual || 0);
      formik.setFieldValue('portaria671', values.portaria671);
      formik.setFieldValue('integradorREP', values.integradorRep === 1);
      formik.setFieldValue('moduloEPI', values.moduloEpi);

      await loadCliente(values);
    }

    formik.setFieldValue('tipoAtualizacao', (isShow && values.tipoAtualizacao ? values.tipoAtualizacao : (fromNS ? 'A' : values.tipoAtualizacao)));
    formik.setFieldValue('numeroSerie', values.numeroSerie);
    formik.setFieldValue('bancoDeDados', values.tipoBancoDados);
    formik.setFieldValue('faixaFuncionarios', values.faixaFuncionarios);
    formik.setFieldValue('usuariosAdicionais', values.quantidadeUsuarios - 1 || values.usuariosAdicionais);
    formik.setFieldValue('licenciado', values.licenciado === 1);
    formik.setFieldValue('moduloAvaliacao', values.moduloAvaliacao === 1);
    formik.setFieldValue('moduloDocumentos', values.moduloDocumentos === 1);
    formik.setFieldValue('portaria671', values.portaria671 === 1);
    formik.setFieldValue('integradorREP', values.integradorRep === 1);
    formik.setFieldValue('moduloEPI', values.moduloEpi === 1);
    formik.setFieldValue('acesso', values.integracaoAcesso === 1);
    formik.setFieldValue('folha', values.integracaoFolha === 1);
    formik.setFieldValue('contrato', isShow ? values.temContrato === 1 || values.contrato === 1 : true);
    formik.setFieldValue('motivoCancelamentoOpcao', values.motivoCancelamentoOpcao);
    formik.setFieldValue('motivoCancelamento', values.motivoCancelamento);

    const data = values.dataInicioFaturamento ?? new Date();
    formik.setFieldValue('inicioFaturamento', format(new Date(data), 'yyyy-MM-dd'));

    if (values.dataCancelamento) formik.setFieldValue('dataCancelamento', format(new Date(values.dataCancelamento), 'yyyy-MM-dd'));
    if (values.dataLiberacao) formik.setFieldValue('dataLiberacao', format(new Date(values.dataLiberacao), 'yyyy-MM-dd'));

    setInitialValueFaixaFuncionarios(values.faixaFuncionarios);
    setInitialValueUsuariosAdicionais(values.quantidadeUsuarios - 1 || values.usuariosAdicionais)

    await loadDataPedido(values);
    await loadModalidades(values);
  };

  const loadDataPedido = async (values) => {
    if (values.dataPedido) {
      formik.setFieldValue('dataPedido', format(new Date(values.dataPedido), 'yyyy-MM-dd'));
      return;
    }
    try {
      const { data } = await api.get(`/BmaPonto/${values.pedidoId}`);

      formik.setFieldValue('dataPedido', format(new Date(data.dataPedido), 'yyyy-MM-dd'))
    } catch (error) {
      console.error('Erro ao carregar data do pedido: ' + error);
    }
  }

  const loadModalidades = async (fields) => {
    if (fields.tipoComercializacao !== null) {
      setFieldsFromTipoComercializacao(fields);
    } else {
      const config = getModalidadeConfig(fields);
      setFieldsFromConfig(config);
    }

    checkEnabledFields(fields);
  };

  const setFieldsFromTipoComercializacao = (fields) => {
    formik.setFieldValue('modalidadeComercializacao', isShow ? fields.tipoComercializacao : 2);
    formik.setFieldValue('modalidadeServico', fields.tipoServicoPedido);
    formik.setFieldValue('plano', fields.tipoPlano);
    formik.setFieldValue('tipoLicenciamento', fields.tipoLicenciamento);
    formik.setFieldValue('tipoPlano', fields.tipoInstalacao);
  };

  const getModalidadeConfig = (fields) => {
    const config = {
      0: {
        modalidadeComercializacao: isShow ? 1 : 2,
        modalidadeServico: 1,
        plano: 1,
        tipoLicenciamento: Number(fields.valorContratoAtual || 0) > 0 ? 2 : 1,
        tipoPlano: fields.tipoInstalacao,
      },
      1: {
        modalidadeComercializacao: 2,
        modalidadeServico: 2,
        plano: 1,
        tipoPlano: fields.tipoInstalacao,
      },
      2: {
        modalidadeComercializacao: 2,
        modalidadeServico: 2,
        plano: 2,
        tipoPlano: fields.tipoInstalacao,
      },
    };

    return config[fields.tipoInstalacao];
  };

  const setFieldsFromConfig = (config) => {
    Object.keys(config).forEach((field) => {
      formik.setFieldValue(field, config[field]);
    });
  };

  const checkEnabledFields = (fields) => {
    if (nivelUsuario !== '1' && (fields.tipoComercializacao === 1 || (fields.tipoInstalacao === 0 && fields.tipoComercializacao === null))) {
      setIsPlanoEnabled(false);
    } else if (nivelUsuario !== '1' && ((fields.tipoComercializacao === 2 && fields.tipoServicoPedido === 1))) {
      setIsPlanoEnabled(false);
    } else {
      setIsPlanoEnabled(true);
    }

    if (nivelUsuario !== '1' && (fields.tipoInstalacao === 0 || fields.tipoComercializacao === 1) && Number(fields.valorContratoAtual || 0) > 0) {
      setIsLicenciadoEnabled(true);
    } else {
      setIsLicenciadoEnabled(false);
    }
  }

  const loadCliente = async (fields) => {
    if (fields.cliente) {
      formik.setFieldValue('nomeFantasia', fields.cliente.nomeFantasia);
      formik.setFieldValue('razaoSocial', fields.cliente.razaoSocial);
      formik.setFieldValue('numeroInscricao', fields.cliente.numeroInscricao);
    } else {
      const responseCliente = await api.get(`/Cliente/${fields.clienteId}`);

      formik.setFieldValue('nomeFantasia', responseCliente.data.nomeFantasia);
      formik.setFieldValue('numeroInscricao', responseCliente.data.numeroInscricao);
      formik.setFieldValue('razaoSocial', responseCliente.data.razaoSocial);
    }
  }

  const handleContratoChange = (e) => {
    if (formik.values.modalidadeComercializacao === 2) {
      formik.setFieldValue('contrato', true);
      return;
    }

    formik.handleChange(e);
  }

  const handleModalidadeComercializacaoChange = (e) => {
    formik.handleChange(e);

    if (e.target.value === 1) {
      formik.setFieldValue('modalidadeServico', 1);
      formik.setFieldValue('plano', 1);

      if (nivelUsuario !== '1') {
        setIsPlanoEnabled(false);
      }
    } else {
      formik.setFieldValue('contrato', true);
      if (!formik.values.modalidadeServico || formik.values.modalidadeServico === 2) setIsPlanoEnabled(true);
    }
  }

  const handleModalidadeServicoChange = (e) => {
    tipoAtualizacaoCalculadoPeloUsuario.current = true;

    formik.handleChange(e);

    if (e.target.value === 1) {
      formik.setFieldValue('plano', 1);
      if (nivelUsuario !== '1') setIsPlanoEnabled(false);
      formik.setFieldValue('inicioFaturamento', format(new Date(), 'yyyy-MM-dd'));
    } else {
      setIsPlanoEnabled(true);
      (formik.values.tipoPedido === 0) ? formik.setFieldValue('inicioFaturamento', format(addDays(new Date(), 30), 'yyyy-MM-dd')) : formik.setFieldValue('inicioFaturamento', format(new Date(), 'yyyy-MM-dd'));
    }
  }

  const handleFaixaFuncionariosChange = (e) => {
    tipoAtualizacaoCalculadoPeloUsuario.current = true;

    const inputValue = parseInt(e.target.value, 10);
    const current = formik.values.faixaFuncionarios;

    if (isTyping) {
      setIsTyping(false);
      return;
    }

    let nextValue = current;

    if (inputValue > current) {
      if (current < 100) {
        if (current === 10) nextValue = 25;
        else if (current === 25) nextValue = 50;
        else if (current === 50) nextValue = 75;
        else if (current === 75) nextValue = 100;
      } else {
        nextValue = current + 50;
      }
    } else if (inputValue < current) {
      if (current <= 25) nextValue = 10;
      else if (current === 50) nextValue = 25;
      else if (current === 75) nextValue = 50;
      else if (current === 100) nextValue = 75;
      else if (current > 100) nextValue = current - 50;
    }

    formik.setFieldValue('faixaFuncionarios', nextValue);
  };

  useEffect(() => {
    const fetchUltimoPedidoLicenca = async () => {
      if (!lastLicenca || !lastLicenca.pedidoId) {
        setUltimoPedidoLicenca(null);
        return;
      }

      try {
        const { data } = await api.get(`/BmaPonto/${lastLicenca.pedidoId}`);
        setUltimoPedidoLicenca(data);
      } catch (error) {
        console.error('Erro ao carregar dados do pedido anterior:', error);
        setUltimoPedidoLicenca(null);
      }
    };

    fetchUltimoPedidoLicenca();
  }, [lastLicenca?.pedidoId]);

  useEffect(() => {
    if (isShow || !tipoAtualizacaoCalculadoPeloUsuario.current) {
      return;
    }

    if (formik.values.tipoPedido !== 1) {
      if (formik.values.tipoAtualizacao) formik.setFieldValue('tipoAtualizacao', 'A');
      return;
    }

    const modalidadeBaseAnterior = (() => {
      if (!lastLicenca) return null;

      if (lastLicenca.tipoServicoPedido !== null && lastLicenca.tipoServicoPedido !== undefined) {
        return Number(lastLicenca.tipoServicoPedido);
      }

      if (lastLicenca.tipoInstalacao === null || lastLicenca.tipoInstalacao === undefined) {
        return null;
      }

      const tipoInst = Number(lastLicenca.tipoInstalacao);

      if (tipoInst === 0) return 1;
      if (tipoInst === 1 || tipoInst === 2) return 2;

      return null;
    })();

    const modalidadeAtual = formik.values.modalidadeServico !== null && formik.values.modalidadeServico !== undefined
      ? Number(formik.values.modalidadeServico)
      : null;

    const modalidadeChanged =
      modalidadeBaseAnterior !== null &&
      modalidadeAtual !== null &&
      modalidadeAtual !== modalidadeBaseAnterior;

    const faixaAtual = Number(formik.values.faixaFuncionarios || 0);
    const faixaAnterior = Number(
      ultimoPedidoLicenca?.faixaFuncionarios ??
      lastLicenca.faixaFuncionarios ??
      0
    );

    const usuariosAtual = Number(formik.values.usuariosAdicionais || 0);
    const usuariosAnteriorPedido = (() => {
      if (
        ultimoPedidoLicenca &&
        typeof ultimoPedidoLicenca.usuariosAdicionais !== 'undefined' &&
        ultimoPedidoLicenca.usuariosAdicionais !== null
      ) {
        return Number(ultimoPedidoLicenca.usuariosAdicionais);
      }

      if (
        ultimoPedidoLicenca &&
        typeof ultimoPedidoLicenca.quantidadeUsuarios !== 'undefined' &&
        ultimoPedidoLicenca.quantidadeUsuarios !== null
      ) {
        return Number(ultimoPedidoLicenca.quantidadeUsuarios) - 1;
      }

      return NaN;
    })();
    const usuariosTotalBase = Number(
      lastLicenca.totalUsuarios ??
      lastLicenca.quantidadeUsuarios ??
      usuariosAnteriorPedido ??
      1
    );
    const usuariosAnteriorBase = !Number.isNaN(usuariosAnteriorPedido)
      ? usuariosAnteriorPedido
      : usuariosTotalBase - 1;
    const usuariosAnterior = usuariosAnteriorBase >= 0 ? usuariosAnteriorBase : 0;

    const faixaAumentou = faixaAtual > faixaAnterior;
    const usuariosAumentaram = usuariosAtual > usuariosAnterior;

    let novoTipo = 'A';
    if (modalidadeChanged) novoTipo = 'M';
    else if (faixaAumentou || usuariosAumentaram) novoTipo = 'AM';

    if (novoTipo) {
      formik.setFieldValue('tipoAtualizacao', novoTipo);
    }
  }, [
    formik.values.faixaFuncionarios,
    formik.values.modalidadeServico,
    formik.values.tipoPedido,
    formik.values.usuariosAdicionais,
  ]);

  useEffect(() => {
    const fetchContratoSituacao = async () => {
      if (!lastLicenca || !lastLicenca.numeroSerie) {
        setValorContratoSituacao(0);
        return;
      }

      const valor = await buscarValorContratoAtual(lastLicenca.numeroSerie);
      setValorContratoSituacao(valor);
    };

    fetchContratoSituacao();
  }, [lastLicenca?.numeroSerie]);

  useEffect(() => {
    if (
      nivelUsuario !== '1' ||
      formik.values.tipoPedido !== 1 ||
      !lastLicenca ||
      Object.keys(lastLicenca).length === 0
    ) {
      setValorDiferencaMensal(currencyFormatter.format(0));
      return;
    }

    const valorAtual = parseNumberCurrency(formik.values.valorMensal) || 0;
    const referenciaContrato = valorContratoSituacao;

    const diferenca = valorAtual - referenciaContrato;
    setValorDiferencaMensal(currencyFormatter.format(diferenca));
  }, [
    currencyFormatter,
    formik.values.tipoPedido,
    formik.values.valorMensal,
    lastLicenca,
    nivelUsuario,
    parseNumberCurrency,
    valorContratoSituacao,
  ]);

  useEffect(() => {
    if (!id || formik.values.statusPedido === null || !formik.values.dataLiberacao) return;

    async function fetchLastLicenca() {
      try {
        const { data } = await api.get(
          `/LicencaPonto/parametros-consulta?id=${idDistribuidor}&nivel=${nivelUsuario}&tipo=ns&args=${formik.values.numeroSerie}`
        );

        const ordered = data.sort((a, b) =>
          new Date(b.dataLiberacao) - new Date(a.dataLiberacao)
        );

        let licenca = null;

        if (formik.values.statusPedido === 0) {
          licenca = ordered[0];
        } else if (formik.values.statusPedido > 0 && formik.values.dataLiberacao) {
          licenca = ordered.find((l) =>
            new Date(l.dataLiberacao) < new Date(formik.values.dataLiberacao)
          );
        }

        if (!licenca) return;

        setLastLicenca(licenca);
      } catch (e) {
        console.log(e);
      }
    }

    fetchLastLicenca();
  }, [id, formik.values.statusPedido]);

  useEffect(() => {
    const loadData = async () => {
      if (!id) return;

      try {
        setIsLoading(true)
        const response = await api.get(`/BmaPonto/${id}`);
        loadFields(response.data, true)
      } catch (e) {
        console.log(e)
      } finally {
        setIsLoading(false)
      }
    }

    loadData();
  }, [id])

  useEffect(() => {
    const getClientes = async (id) => {
      if (!id) return;

      try {
        const { data } = await api.get(`/Cliente/${id}/A`);

        const registrosFiltrados = data
          .filter((cliente) => cliente.statusEmpresa === 1)
          .sort((a, b) => a.razaoSocial.localeCompare(b.razaoSocial));

        setClientes(registrosFiltrados);
      } catch (error) {
        console.error("Erro ao buscar clientes:", error);
      }
    };
    getClientes(formik.values.distribuidor);
  }, [formik.values.distribuidor]);

  useEffect(() => {
    if (selectedNS) atualizarLicencaPorNS();
  }, [selectedNS])

  useEffect(() => {
    api.get('/TipoServico/').then((r) => setTiposServico(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id && formik.values.tipoServicoId === null && tipoServicoPadrao) {
      formik.setFieldValue('tipoServicoId', tipoServicoPadrao);
    }
  }, [id, tipoServicoPadrao]);

  useEffect(() => {
    async function getVersaoAtual() {
      const response = await api.get(`/Versao/retornar-versao-atual?tipoSistema=1`);

      formik.setFieldValue('versao', response.data.numeroVersao)
      formik.setFieldValue('release', response.data.release)
    }

    getVersaoAtual();

    if (formik.values.statusPedido === 0) {
      setPedidoLiberado(false);
    }
  }, [])

  useEffect(() => {
    if (formik.values.plano === null || formik.values.faixaFuncionarios === 0 || nivelUsuario === '1') return;
    handleVerificarTotaisClick();
  }, [formik.values.plano, formik.values.faixaFuncionarios, formik.values.usuariosAdicionais])

  useEffect(() => {
    if (formik.values.plano === 1 || formik.values.plano === 2) {
      formik.setFieldValue('contrato', true);
    }
  }, [formik.values.plano])

  useEffect(() => {
    setDadosTabs({
      distribuidor: formik.values.distribuidor,
      cliente: formik.values.cliente,
      nomeFantasia: formik.values.nomeFantasia,
      numeroSerie: formik.values.numeroSerie,
      observacaoNotaFiscal: formik.values.observacaoNotaFiscal,
    })
  }, [
    formik.values.distribuidor,
    formik.values.cliente,
    formik.values.nomeFantasia,
    formik.values.numeroSerie,
    formik.values.observacaoNotaFiscal,
  ])

  useEffect(() => {
    if (formik.isSubmitting && Object.keys(formik.errors).length) {
      setMensagem('Preencha os campos obrigatórios.')
      setTipoMensagem('danger');
      setShowMensagem(true)
    }
  }, [formik.errors])

  useEffect(() => {
    if (formik.submitCount === 0) return;
    const errorKeys = Object.keys(formik.errors);
    if (!errorKeys.length) return;
    const selector = errorKeys.map(name => `[name="${name}"]`).join(', ');
    const el = document.querySelector(selector);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus?.();
    }
  }, [formik.submitCount])

  useEffect(() => {
    if (formik.values.plano === 1 || formik.values.plano === 2) {
      formik.setFieldValue('bancoDeDados', 1);
    }
  }, [formik.values.plano])

  useEffect(() => {
    async function loadRevenda() {
      try {
        if (distribuidores.length === 0) {
          await getDistribuidores();
        } else if (distribuidores.length === 1) {
          formik.setFieldValue('distribuidor', distribuidores[0].id);
          setIsRevendaEnabled(false)
        }
      } catch (error) {
        console.error('Erro ao buscar distribuidores:', error);
      }
    }

    loadRevenda()
  }, [distribuidores])

  if (isLoading) return <Loading />;

  return (
    <Box>
      {showMensagem && <Mensagem mensagem={mensagem} setMensagem={setMensagem} variant={tipoMensagem} />}

      {modalLiberacao.show &&
        <ModalLiberacao
          pedido={modalLiberacao.pedido}
        />
      }

      {showModalNS && formik.values.tipoPedido === 1 && listaNS.length > 0 &&
        <ModalNS
          modalNS={showModalNS}
          setModalNS={setShowModalNS}
          numeroSerie={formik.values.numeroSerie}
          setNumeroSerie={(value) => formik.setFieldValue('numeroSerie', value)}
          titleModal={formik.values.nomeFantasia}
          listaNS={listaNS}
          setNSSelecionada={setSelectedNS}
        />
      }

      <Container sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <Tabs
            sx={{ backgroundColor: 'ghostwhite', boxShadow: '0 0 3px 0 rgba(0, 0, 0, 0.05)' }}
            value={value}
            onChange={handleTabChange}
            aria-label="basic tabs example"
          >
            <Tab label="Pedido" {...tabProps(0)} />
            <Tab label="Contrato" {...tabProps(1)} disabled={!formik.values.contrato || !id} />
            <Tab label="Serviço" {...tabProps(2)} disabled={!id} />
          </Tabs>
          <Typography component='h1' variant='h5' sx={{ margin: '0.6rem 13%', fontWeight: 400, color: '#a3a3a3' }}>Pedido - Ponto</Typography>
        </Box>
        <Box>
          <Paper sx={{ backgroundColor: '#F7F8F8', width: '70rem' }} elevation={2}>
            <CustomTabPanel value={value} index={0}>
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'end', mb: 2, mt: -1 }}>
                {(isShow && formik.values.statusPedido === 0 && formik.values.dataCancelamento === null) &&
                  <Button
                    variant='text'
                    startIcon={<DriveFolderUploadIcon />}
                    onClick={() => setModalLiberacao({ show: true, pedido: { id: id, cliente: { razaoSocial: formik.values.razaoSocial, numeroInscricao: formik.values.numeroInscricao }, contrato: formik.values.contrato, tipoComercializacao: formik.values.modalidadeComercializacao, tipoPlano: formik.values.plano } })}
                    color='success'
                    disabled={nivelUsuario !== '1' || pedidoLiberado}
                  >
                    Liberar Pedido
                  </Button>
                }
                {isShow && formik.values.statusPedido === 0 && (nivelUsuario === '1' || nivelUsuario === '3') &&
                  <Tooltip title='Editar'>
                    <IconButton onClick={() => navigate(`/pedidoponto/${id}`)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
              </Box>
              <Box
                component='form'
                onSubmit={formik.handleSubmit}
                sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
              >
                <FormSectionBox>
                  <FormSectionTitle label="Dados do Pedido" />
                  <Container sx={{ display: 'flex', flexDirection: 'column', my: 3, justifyContent: 'center' }}>
                    <BoxBase>
                      <FormControlBase>
                        <Box display="flex" alignItems="center">
                          <TextFieldBase
                            label="Número de Série"
                            name="numeroSerie"
                            value={formik.values.numeroSerie}
                            onChange={formik.handleChange}
                            onBlur={() => atualizarLicencaPorNS(true)}
                            error={formik.touched.numeroSerie && Boolean(formik.errors.numeroSerie)}
                            helperText={formik.touched.numeroSerie && formik.errors.numeroSerie}
                            width='20rem'
                            textAlign='center'
                            disabled={isShow}
                          />
                          <Tooltip title="Para licenças novas, o Número de Série deve ser 0" arrow>
                            <InfoOutlinedIcon sx={{ ml: -4, zIndex: 2, color: 'gray', opacity: 0.6, fontSize: 18, cursor: 'default', cursor: 'pointer', ":hover": { opacity: 0.9 } }} />
                          </Tooltip>
                        </Box>

                        <ButtonBase
                          variant="contained"
                          onClick={() => handleSelecionarNS()}
                          sx={{ marginLeft: 1.7, width: '10rem', fontSize: '0.75rem', maxHeight: '3.5rem' }}
                          disabled={!formik.values.cliente || formik.values.quantidadeLicencas === 0 || isShow}
                        >
                          Selecionar NS
                        </ButtonBase>
                      </FormControlBase>
                    </BoxBase>

                    <BoxBase>
                      <FormControlBase>
                        <InputLabelSelect id="distribuidor">Revenda</InputLabelSelect>
                        <SelectBase
                          id="distribuidor"
                          label="Revenda"
                          name="distribuidor"
                          value={formik.values.distribuidor}
                          onChange={(e) => handleDistribuidorChange(e)}
                          onBlur={formik.handleBlur}
                          error={formik.touched.distribuidor && Boolean(formik.errors.distribuidor)}
                          helperText={formik.touched.distribuidor && formik.errors.distribuidor}
                          isShow={isShow || !isRevendaEnabled}
                          width='20rem'
                        >
                          {distribuidores.map((distribuidor) => (
                            <MenuItem
                              key={distribuidor.id}
                              value={distribuidor.id}
                            >
                              {distribuidor.razaoSocial}
                            </MenuItem>
                          ))}
                        </SelectBase>
                      </FormControlBase>
                      <FormControlBase>
                        <InputLabelSelect id="cliente">Cliente</InputLabelSelect>
                        <SelectBase
                          id="cliente"
                          label="Cliente"
                          name="cliente"
                          value={formik.values.cliente}
                          onChange={(e) => handleClienteChange(e)}
                          onBlur={formik.handleBlur}
                          error={formik.touched.cliente && Boolean(formik.errors.cliente)}
                          helperText={formik.touched.cliente && formik.errors.cliente}
                          isShow={isShow}
                          width='21.5rem'
                        >
                          {clientes.map((cliente) => (
                            <MenuItem
                              key={cliente.id}
                              value={cliente.id}
                            >
                              {cliente.razaoSocial} - {cliente.numeroInscricao}
                            </MenuItem>
                          ))}
                        </SelectBase>
                      </FormControlBase>

                      <FormControlBase>
                        <TextFieldBase
                          label="Nome Fantasia"
                          value={formik.values.nomeFantasia}
                          width='20rem'
                          disabled
                        />
                      </FormControlBase>
                    </BoxBase>

                    <FormControlBase sx={{ mb: 2 }}>
                      <InputLabelSelect id="tipoServicoId">Tipo de Serviço</InputLabelSelect>
                      <SelectBase
                        id="tipoServicoId"
                        label="Tipo de Serviço"
                        name="tipoServicoId"
                        value={formik.values.tipoServicoId}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.tipoServicoId && Boolean(formik.errors.tipoServicoId)}
                        helperText={formik.touched.tipoServicoId && formik.errors.tipoServicoId}
                        isShow={isShow}
                        width='100%'
                      >
                        {tiposServico.map((tipo) => (
                          <MenuItem key={tipo.id} value={tipo.id}>
                            {tipo.tituloTipo}
                          </MenuItem>
                        ))}
                      </SelectBase>
                    </FormControlBase>

                    <FormControlBase sx={{ mb: 2 }}>
                      <TextFieldBase
                        label="Observações"
                        name="observacoes"
                        value={formik.values.observacoes}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.observacoes && Boolean(formik.errors.observacoes)}
                        helperText={formik.touched.observacoes && formik.errors.observacoes}
                        width='100%'
                        multiline
                        rows={4}
                        disabled={isShow}
                      />
                    </FormControlBase>

                    <FormControlBase sx={{ mb: 2 }}>
                      <TextFieldBase
                        label="Observação Nota Fiscal"
                        name="observacaoNotaFiscal"
                        value={formik.values.observacaoNotaFiscal}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.observacaoNotaFiscal && Boolean(formik.errors.observacaoNotaFiscal)}
                        helperText={formik.touched.observacaoNotaFiscal && formik.errors.observacaoNotaFiscal}
                        width='100%'
                        multiline
                        rows={3}
                        disabled={isShow}
                      />
                    </FormControlBase>

                    {(formik.values.statusPedido === 2 || formik.values.dataCancelamento !== null) &&
                      <>
                        <br />
                        <BoxBase>
                          <FormControlBase>
                            <TextFieldBase
                              label="Data do Cancelamento"
                              name="dataCancelamento"
                              value={formik.values.dataCancelamento}
                              type="date"
                              onChange={formik.handleChange}
                              onBlur={formik.onBlur}
                              error={formik.touched.dataCancelamento && Boolean(formik.errors.dataCancelamento)}
                              helperText={formik.touched.dataCancelamento && formik.errors.dataCancelamento}
                              width='20rem'
                              disabled={isShow}
                            />
                          </FormControlBase>

                          <FormControlBase>
                            <InputLabelSelect id="motivoCancelamentoOpcao">Motivo do Cancelamento</InputLabelSelect>
                            <SelectBase
                              id='motivoCancelamentoOpcao'
                              name='motivoCancelamentoOpcao'
                              disabled={isShow}
                              onChange={formik.handleChange}
                              onBlur={formik.onBlur}
                              error={formik.touched.motivoCancelamentoOpcao && Boolean(formik.errors.motivoCancelamentoOpcao)}
                              helperText={formik.touched.motivoCancelamentoOpcao && formik.errors.motivoCancelamentoOpcao}
                              label='Motivo do Cancelamento'
                              value={formik.values.motivoCancelamentoOpcao}
                              width='43rem'
                              margin='0 0 0 -5px'
                            >
                              {motivosCancelamento.map((motivo) => (
                                <MenuItem key={motivo.id} value={motivo.id}>
                                  {motivo.descricaoMotivo}
                                </MenuItem>
                              ))}
                            </SelectBase>
                          </FormControlBase>
                        </BoxBase>

                        <br />

                        <FormControlBase>
                          <TextFieldBase
                            label="Detalhes do Cancelamento"
                            name="motivoCancelamento"
                            onChange={formik.handleChange}
                            onBlur={formik.onBlur}
                            error={formik.touched.motivoCancelamento && Boolean(formik.errors.motivoCancelamento)}
                            helperText={formik.touched.motivoCancelamento && formik.errors.motivoCancelamento}
                            value={formik.values.motivoCancelamento}
                            disabled={isShow}
                            width='100%'
                            multiline
                            rows={4}
                          />
                        </FormControlBase>
                      </>
                    }
                  </Container>
                </FormSectionBox>

                <FormSectionBox>
                  <FormSectionTitle label="Dados da Licença" />
                  <Container sx={{ display: 'flex', flexDirection: 'column', my: 3 }}>
                    <BoxBase justifyContent={'flex-start'}>
                      <FormControlBase>
                        <InputLabelSelect id="modalidadeComercializacao">Modalidade de Comercialização</InputLabelSelect>
                        <SelectBase
                          id="modalidadeComercializacao"
                          label="Modalidade de Comercialização"
                          name="modalidadeComercializacao"
                          value={formik.values.modalidadeComercializacao}
                          onChange={handleModalidadeComercializacaoChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.modalidadeComercializacao && Boolean(formik.errors.modalidadeComercializacao)}
                          helperText={formik.touched.modalidadeComercializacao && formik.errors.modalidadeComercializacao}
                          width='32rem'
                          isShow={isShow}
                        >
                          {isShow && formik.values.modalidadeComercializacao === 1 && formik.values.tipoLicenciamento !== 1 && <MenuItem value={1}>Licenciado</MenuItem>}
                          {isShow && formik.values.modalidadeComercializacao === 1 && formik.values.tipoLicenciamento === 1 && <MenuItem value={1}>Licenciamento Anual</MenuItem>}
                          <MenuItem value={2}>Locação</MenuItem>
                        </SelectBase>
                      </FormControlBase>

                      <FormControlBase margin="0 0 0 -1rem">
                        <InputLabelSelect id="modalidadeServico">Modalidade de Serviço</InputLabelSelect>
                        <SelectBase
                          id="modalidadeServico"
                          label="Modalidade de Serviço"
                          name="modalidadeServico"
                          value={formik.values.modalidadeServico}
                          onChange={handleModalidadeServicoChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.modalidadeServico && Boolean(formik.errors.modalidadeServico)}
                          helperText={formik.touched.modalidadeServico && formik.errors.modalidadeServico}
                          width='30.5rem'
                          isShow={isShow}
                        >
                          <MenuItem value={1}>On Premisse</MenuItem>
                          <MenuItem value={2}>SaaS</MenuItem>
                        </SelectBase>
                      </FormControlBase>
                    </BoxBase>

                    <BoxBase>
                      <FormControlBase>
                        <InputLabelSelect id="plano">Plano</InputLabelSelect>
                        <SelectBase
                          id="plano"
                          label="Plano"
                          name="plano"
                          value={formik.values.plano}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.plano && Boolean(formik.errors.plano)}
                          helperText={formik.touched.plano && formik.errors.plano}
                          width='32rem'
                          isShow={isShow || !isPlanoEnabled}
                        >
                          <MenuItem value={1}>Rep C</MenuItem>
                          <MenuItem value={2}>Rep P</MenuItem>
                        </SelectBase>
                      </FormControlBase>

                      <FormControlBase margin="0 0 0 -1rem">
                        <InputLabelSelect id="bancoDeDados">Banco de Dados</InputLabelSelect>
                        <SelectBase
                          id="bancoDeDados"
                          label="Banco de Dados"
                          name="bancoDeDados"
                          value={formik.values.bancoDeDados}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.bancoDeDados && Boolean(formik.errors.bancoDeDados)}
                          helperText={formik.touched.bancoDeDados && formik.errors.bancoDeDados}
                          isShow={isShow}
                          width='30.5rem'
                        >
                          <MenuItem value={1}>MySql</MenuItem>
                          <MenuItem value={2}>SqlServer</MenuItem>
                          <MenuItem value={3}>Oracle</MenuItem>
                        </SelectBase>
                      </FormControlBase>
                    </BoxBase>

                    <BoxBase>
                      <FormControl>
                        <TextFieldBase
                          label="Faixa de Funcionários"
                          name="faixaFuncionarios"
                          type="number"
                          value={formik.values.faixaFuncionarios}
                          onChange={(e) => handleFaixaFuncionariosChange(e)}
                          onBlur={formik.handleBlur}
                          error={formik.touched.faixaFuncionarios && Boolean(formik.errors.faixaFuncionarios)}
                          helperText={formik.touched.faixaFuncionarios && formik.errors.faixaFuncionarios}
                          width='32rem'
                          textAlign='center'
                          onKeyDown={() => setIsTyping(true)}
                          inputProps={{
                            min: 10,
                            step: 1,
                          }}
                          disabled={isShow}
                        />
                      </FormControl>

                      <FormControlBase>
                        <TextFieldBase
                          id="usuariosAdicionais"
                          label="Usuários Adicionais"
                          name="usuariosAdicionais"
                          type="number"
                          textAlign='center'
                          inputProps={{ inputProps: { min: 0 } }}
                          value={formik.values.usuariosAdicionais}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.usuariosAdicionais && Boolean(formik.errors.usuariosAdicionais)}
                          helperText={formik.touched.usuariosAdicionais && formik.errors.usuariosAdicionais}
                          width='32rem'
                          disabled={isShow}
                        />
                      </FormControlBase>
                    </BoxBase>

                    <BoxBase>
                      <FormControlBase>
                        <TextFieldBase
                          label="Início do Faturamento"
                          name="inicioFaturamento"
                          value={formik.values.inicioFaturamento}
                          type="date"
                          onChange={formik.handleChange}
                          onBlur={formik.onBlur}
                          error={formik.touched.inicioFaturamento && Boolean(formik.errors.inicioFaturamento)}
                          helperText={formik.touched.inicioFaturamento && formik.errors.inicioFaturamento}
                          width='32rem'
                          disabled={isShow || nivelUsuario !== '1'}
                        />
                      </FormControlBase>

                      {formik.values.tipoPedido === 1 && (
                        <FormControlBase margin="0 0 0 -2rem">
                          <InputLabelSelect id="tipoAtualizacao">Tipo de Atualização</InputLabelSelect>
                          <SelectBase
                            id="tipoAtualizacao"
                            label="Tipo de Atualização"
                            name="tipoAtualizacao"
                            value={formik.values.tipoAtualizacao}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            width='30.5rem'
                            disabled={isShow}
                          >
                            <MenuItem value="A">Atualização</MenuItem>
                            <MenuItem value="M">Migração</MenuItem>
                            <MenuItem value="AM">Ampliação</MenuItem>
                          </SelectBase>
                        </FormControlBase>
                      )}
                    </BoxBase>

                    <FormikProvider value={formik}>
                      <Grid container spacing={2} sx={{ pb: 2, pl: 4, mt: 1, display: 'flex' }}>
                        <Grid item xs={3}>
                          <CheckboxBase
                            name="portaria671"
                            checked={formik.values.portaria671}
                            onChange={formik.handleChange}
                            disabled={isShow}
                            label="Portaria 671"
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <CheckboxBase
                            name="integradorREP"
                            checked={formik.values.integradorREP}
                            onChange={formik.handleChange}
                            disabled={isShow}
                            label="Integrador REP"
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <CheckboxBase
                            name="moduloEPI"
                            checked={formik.values.moduloEPI}
                            onChange={formik.handleChange}
                            disabled={isShow}
                            label="Módulo EPI"
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <CheckboxBase
                            name="acesso"
                            checked={formik.values.acesso}
                            onChange={formik.handleChange}
                            disabled={isShow}
                            label="Acesso"
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <CheckboxBase
                            name="folha"
                            checked={formik.values.folha}
                            onChange={formik.handleChange}
                            disabled={isShow}
                            label="Folha"
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <CheckboxBase
                            name="contrato"
                            checked={formik.values.contrato}
                            onChange={handleContratoChange}
                            disabled={isShow}
                            label="Contrato"
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <CheckboxBase
                            name="moduloAvaliacao"
                            checked={formik.values.moduloAvaliacao}
                            onChange={formik.handleChange}
                            disabled={isShow}
                            label="Módulo Avaliações"
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <CheckboxBase
                            name="moduloDocumentos"
                            checked={formik.values.moduloDocumentos}
                            onChange={formik.handleChange}
                            disabled={isShow}
                            label="Gestão de Documentos"
                          />
                        </Grid>
                        {nivelUsuario === '1' &&
                          <Grid item xs={3}>
                            <CheckboxBase
                              name="licenciado"
                              checked={formik.values.licenciado}
                              onChange={formik.handleChange}
                              disabled={isShow || id}
                              label="Licenciado"
                            />
                          </Grid>
                        }
                      </Grid>
                    </FormikProvider>
                  </Container>
                </FormSectionBox>

                {lastLicenca?.id && <LicencaSection licenca={lastLicenca} dataPedido={formik.values.dataPedido} isPonto />}

                <FormSectionBox>
                  <FormSectionTitle label="Totais" />
                  <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Grid container spacing={2} sx={{ p: 4 }}>
                      <Grid item xs={4}>
                        <FormControl>
                          <CurrencyInput
                            customInput={TextFieldBase}
                            label="Valor Mensal"
                            name="valorMensal"
                            value={formik.values.valorMensal}
                            onValueChange={(value) => formik.setFieldValue('valorMensal', value)}
                            intlConfig={{ locale: 'pt-BR', currency: 'BRL' }}
                            disabled={nivelUsuario !== '1' || isShow}
                            textAlign="center"
                            sx={{ mb: 1.5, width: '20rem' }}
                            decimalScale={2}
                            decimalsLimit={2}
                          />
                          {nivelUsuario === '1' && formik.values.tipoPedido === 1 && (
                            <TextFieldBase
                              label="Diferença vs Última Licença"
                              value={valorDiferencaMensal}
                              width='20rem'
                              disabled
                              sx={{ mt: 1.5 }}
                            />
                          )}
                        </FormControl>

                      </Grid>

                      <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'end', pb: 2 }}>
                        <ButtonBase variant="outlined" onClick={() => handleVerificarTotaisClick()} startIcon={<AttachMoneyIcon />} disabled={isShow} >
                          Verificar Totais
                        </ButtonBase>
                      </Grid>
                    </Grid>
                  </Box>
                </FormSectionBox>

                {id && formik.values.statusPedido === 0 &&
                  <InscricoesAdicionais clienteId={formik.values.cliente} pedidoId={id} />
                }

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                  {isShow ? (
                    <ButtonBase variant="outlined" onClick={() => navigate('/pedidos')}>
                      Voltar
                    </ButtonBase>
                  ) : (
                    <>
                      <ButtonBase variant="outlined" onClick={() => navigate('/pedidos')}>
                        Cancelar
                      </ButtonBase>
                      <ButtonBase type="submit" variant="contained" disabled={!isSaveEnabled || isSaving}>
                        Salvar
                      </ButtonBase>
                    </>
                  )}
                </Box>

              </Box>
            </CustomTabPanel>

            <CustomTabPanel value={value} index={1}>
              <ContratoTab isShow={isShow} />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={2}>
              <ServicoTab isShow={isShow || nivelUsuario !== "1"} />
            </CustomTabPanel>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
}
